import { z } from 'zod'
import { TRAINER_CONTRACT_VERSION, trainerReadSideEffectsSchema } from './trainer-event-read'

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const trainerInvoiceReadQuerySchema = z.object({
  orgId: optionalQueryString,
  userId: optionalQueryString,
  includeArchived: optionalQueryString,
  include_archived: optionalQueryString,
  page: optionalQueryString,
  per_page: optionalQueryString,
  search: optionalQueryString,
  status: optionalQueryString,
}).passthrough()

export const trainerInvoiceUploadSchema = z.object({
  uploadId: z.string().nullable(),
  filename: z.string().nullable(),
  contentType: z.string().nullable(),
  fileSizeBytes: z.number().finite().nullable(),
  status: z.enum(['issued', 'completed']).nullable(),
  uploadTouched: z.boolean(),
  r2Touched: z.literal(false),
}).passthrough()

export const trainerInvoiceLineItemSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  invoiceId: z.string().nullable(),
  name: z.string(),
  quantity: z.number().finite().nullable(),
  amount: z.number().finite().nullable(),
  description: z.string().nullable(),
  externalItemId: z.string().nullable(),
  courseTypeId: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const trainerInvoiceSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  createdByUserId: z.string().min(1),
  userId: z.string().min(1),
  date: z.string(),
  dueDate: z.string(),
  amount: z.number().finite().nullable(),
  paid: z.boolean(),
  invoiceType: z.enum(['personal', 'vjal']),
  clientName: z.string().nullable(),
  clientEmail: z.string().nullable(),
  billingAddress: z.string().nullable(),
  directExpense: z.number().finite().nullable(),
  invoiceImageName: z.string().nullable(),
  eventId: z.string().nullable(),
  eventName: z.string().nullable(),
  courseTypeId: z.string().nullable(),
  invoiceItemName: z.string().nullable(),
  description: z.string().nullable(),
  quantity: z.number().finite().nullable(),
  pricePerQuantity: z.number().finite().nullable(),
  idempotencyKey: z.string().nullable(),
  upload: trainerInvoiceUploadSchema,
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lineItems: z.array(trainerInvoiceLineItemSchema).optional(),
}).passthrough()

export const trainerInvoiceReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.union([
    z.tuple([z.literal('trainer_invoices')]),
    z.tuple([z.literal('trainer_invoices'), z.literal('trainer_invoice_items')]),
  ]),
  projection: z.enum(['trainer-invoice-summary', 'trainer-invoice-detail-with-items']),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const trainerInvoiceReadSideEffectsSchema = trainerReadSideEffectsSchema.passthrough()

export const trainerInvoiceScopeSchema = z.object({
  mode: z.enum(['own', 'org']),
  orgId: z.string().min(1),
  userId: z.string().nullable(),
  includeOrgWide: z.boolean(),
}).passthrough()

const trainerInvoicePaginationSchema = z.object({
  page: z.number().int().min(1),
  perPage: z.number().int().min(1),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
}).passthrough()

const trainerInvoiceListResponseShape = {
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  invoices: z.array(trainerInvoiceSummarySchema),
  totalAmount: z.number().finite(),
  paidAmount: z.number().finite(),
  unpaidAmount: z.number().finite(),
  overdueCount: z.number().int().nonnegative().optional(),
  overdueAmount: z.number().finite().optional(),
  createdThisWeekCount: z.number().int().nonnegative().optional(),
  totalCount: z.number().int().nonnegative().optional(),
  paidCount: z.number().int().nonnegative().optional(),
  unpaidCount: z.number().int().nonnegative().optional(),
  pagination: trainerInvoicePaginationSchema.optional(),
  invoiceEvidence: trainerInvoiceReadEvidenceSchema,
  sideEffects: trainerInvoiceReadSideEffectsSchema,
  invoiceScope: trainerInvoiceScopeSchema.optional(),
}

export const trainerInvoiceListResponseSchema = z.object(trainerInvoiceListResponseShape).passthrough()

export const trainerInvoiceCurrentUserListResponseSchema = z.object({
  ...trainerInvoiceListResponseShape,
  invoiceScope: trainerInvoiceScopeSchema,
}).passthrough()

export const trainerInvoiceDetailResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  invoice: trainerInvoiceSummarySchema,
  invoiceEvidence: trainerInvoiceReadEvidenceSchema,
  sideEffects: trainerInvoiceReadSideEffectsSchema,
}).passthrough()

const trainerInvoiceProviderRequestSchema = z.object({
  status: z.string(),
  provider: z.string(),
  action: z.string(),
  actionType: z.string(),
  providerMode: z.string(),
  outboxId: z.union([z.string(), z.number()]).nullable(),
  attemptCount: z.number().int().nonnegative(),
  maxAttempts: z.number().int().nonnegative(),
  nextAttemptAt: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  providerAuditId: z.union([z.string(), z.number()]).nullable(),
  lastErrorCode: z.string().nullable(),
  lastErrorSummary: z.string().nullable(),
  reason: z.string(),
  providerTouched: z.literal(false),
  paymentProviderTouched: z.literal(false),
  rawProviderPayloadReturned: z.literal(false),
}).passthrough()

const trainerInvoiceLifecycleSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  invoiceId: z.string().min(1),
  documentDownload: z.object({
    status: z.string(),
    route: z.string(),
    contentType: z.string(),
    backendGenerated: z.boolean(),
    providerTouched: z.literal(false),
    rawProviderPayloadReturned: z.literal(false),
  }).passthrough(),
  pdf: z.object({
    status: z.string(),
    provider: z.string(),
    route: z.string(),
    contentType: z.string(),
    backendGenerated: z.boolean(),
    providerTouched: z.literal(false),
    r2Touched: z.literal(false),
    rawProviderPayloadReturned: z.literal(false),
    reason: z.string(),
  }).passthrough(),
  email: z.object({
    status: z.string(),
    provider: z.string(),
    reason: z.string(),
    providerTouched: z.literal(false),
    rawProviderPayloadReturned: z.literal(false),
  }).passthrough(),
  paymentLink: z.object({
    status: z.string(),
    provider: z.string(),
    route: z.string(),
    requiresPlatformLogin: z.boolean(),
    backendGenerated: z.boolean(),
    reason: z.string(),
    providerTouched: z.literal(false),
    paymentProviderTouched: z.literal(false),
    rawProviderPayloadReturned: z.literal(false),
  }).passthrough(),
  paymentProvider: z.object({
    status: z.string(),
    provider: z.string(),
    providerInvoiceId: z.null(),
    providerPaymentStatus: z.string(),
    providerVoidAvailable: z.boolean(),
    providerRefundAvailable: z.boolean(),
    reason: z.string(),
    paymentProviderTouched: z.literal(false),
    rawProviderPayloadReturned: z.literal(false),
  }).passthrough(),
  providerRequests: z.object({
    email: trainerInvoiceProviderRequestSchema,
    paymentLink: trainerInvoiceProviderRequestSchema,
    void: trainerInvoiceProviderRequestSchema,
    refund: trainerInvoiceProviderRequestSchema,
    qboCustomer: trainerInvoiceProviderRequestSchema,
    qboInvoice: trainerInvoiceProviderRequestSchema,
  }).passthrough(),
  upload: z.object({
    status: z.string(),
    uploadId: z.string().nullable(),
    filename: z.string().nullable(),
    uploadTouched: z.boolean(),
    r2Touched: z.literal(false),
    reason: z.string(),
  }).passthrough(),
}).passthrough()

const trainerInvoiceReconciliationSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  invoiceId: z.string().min(1),
  invoiceType: z.enum(['personal', 'vjal']),
  amount: z.record(z.unknown()),
  directExpense: z.record(z.unknown()),
  paymentProvider: z.record(z.unknown()),
  settlementShares: z.record(z.unknown()),
}).passthrough()

const trainerInvoiceCutoverSummarySchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  invoiceId: z.string().min(1),
  cutoverReady: z.boolean(),
  ownedReadyCount: z.number().int().nonnegative(),
  providerGatedCount: z.number().int().nonnegative(),
  blockedCount: z.number().int().nonnegative(),
  capabilities: z.array(z.record(z.unknown())),
  nextActions: z.array(z.string()),
  blockingGates: z.array(z.string()),
  paymentProviderTouched: z.literal(false),
  settlementProviderTouched: z.literal(false),
  rawProviderPayloadReturned: z.literal(false),
}).passthrough()

export const trainerInvoiceLifecycleResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  invoice: trainerInvoiceSummarySchema,
  invoiceLifecycle: trainerInvoiceLifecycleSchema,
  invoiceReconciliation: trainerInvoiceReconciliationSchema,
  invoiceCutoverSummary: trainerInvoiceCutoverSummarySchema,
  invoiceEvidence: trainerInvoiceReadEvidenceSchema,
  sideEffects: trainerInvoiceReadSideEffectsSchema,
}).passthrough()

export type TrainerInvoiceReadQuery = z.infer<typeof trainerInvoiceReadQuerySchema>
export type TrainerInvoiceUpload = z.infer<typeof trainerInvoiceUploadSchema>
export type TrainerInvoiceLineItem = z.infer<typeof trainerInvoiceLineItemSchema>
export type TrainerInvoiceSummary = z.infer<typeof trainerInvoiceSummarySchema>
export type TrainerInvoiceReadEvidence = z.infer<typeof trainerInvoiceReadEvidenceSchema>
export type TrainerInvoiceReadSideEffects = z.infer<typeof trainerInvoiceReadSideEffectsSchema>
export type TrainerInvoiceScope = z.infer<typeof trainerInvoiceScopeSchema>
export type TrainerInvoiceListResponse = z.infer<typeof trainerInvoiceListResponseSchema>
export type TrainerInvoiceCurrentUserListResponse = z.infer<typeof trainerInvoiceCurrentUserListResponseSchema>
export type TrainerInvoiceDetailResponse = z.infer<typeof trainerInvoiceDetailResponseSchema>
export type TrainerInvoiceLifecycleResponse = z.infer<typeof trainerInvoiceLifecycleResponseSchema>
